import { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, ArrowClockwise, Check, Flag } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABELS = {
  eliminated: "Eliminated (Group Stage)",
  group_stage: "Group Stage",
  round_of_32: "Round of 32",
  round_of_16: "Round of 16",
  quarter_final: "Quarter Final",
  semi_final: "Semi Final",
  final: "Final",
  winner: "Winner"
};

const getFlagUrl = (countryCode) => {
  if (!countryCode || countryCode === 'XX') return null;
  const codeMap = { 'GB-ENG': 'gb-eng', 'GB-SCT': 'gb-sct', 'GB-WLS': 'gb-wls' };
  const code = codeMap[countryCode] || countryCode.slice(0, 2).toLowerCase();
  return `https://flagcdn.com/w40/${code}.png`;
};

const getSeedStyle = (seed) => {
  switch(seed) {
    case 1: return { bg: '#ECFDF5', color: '#059669', label: 'Fav' };
    case 2: return { bg: '#FFFBEB', color: '#D97706', label: 'Con' };
    default: return { bg: '#EFF6FF', color: '#2563EB', label: 'LS' };
  }
};

export default function TournamentProgressPanel({ getAuthHeader }) {
  const [groups, setGroups] = useState({});
  const [stages, setStages] = useState([]);
  const [pointsSystem, setPointsSystem] = useState({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [successMsg, setSuccessMsg] = useState({});

  useEffect(() => {
    fetchTournament();
  }, []);

  const fetchTournament = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/tournament`, getAuthHeader());
      setGroups(res.data.groups || {});
      setStages(res.data.stages || []);
      setPointsSystem(res.data.points_system || {});
    } catch (err) {
      console.error('Failed to fetch tournament data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateProgress = async (teamId, stage) => {
    setUpdating(prev => ({ ...prev, [teamId]: true }));
    setSuccessMsg(prev => ({ ...prev, [teamId]: null }));
    try {
      const res = await axios.post(
        `${API}/admin/teams/${teamId}/progress`,
        { stage: stage || "none" },
        getAuthHeader()
      );
      setSuccessMsg(prev => ({ ...prev, [teamId]: res.data.entries_updated }));
      // Update local state
      setGroups(prev => {
        const updated = { ...prev };
        for (const g of Object.keys(updated)) {
          updated[g] = updated[g].map(t =>
            t.id === teamId ? { ...t, current_stage: stage || undefined } : t
          );
        }
        return updated;
      });
      setTimeout(() => setSuccessMsg(prev => ({ ...prev, [teamId]: null })), 3000);
    } catch (err) {
      console.error('Failed to update progress:', err);
    } finally {
      setUpdating(prev => ({ ...prev, [teamId]: false }));
    }
  };

  // Goals = tiebreaker tally (regulation + ET only, exclude penalty shootouts).
  // Saves on blur so admins can type freely without the server hammering.
  const updateGoals = async (teamId, raw) => {
    const goals = Math.max(0, Math.min(99, parseInt(raw, 10) || 0));
    setUpdating(prev => ({ ...prev, [teamId]: true }));
    setSuccessMsg(prev => ({ ...prev, [teamId]: null }));
    try {
      const res = await axios.post(
        `${API}/admin/teams/${teamId}/goals`,
        { goals },
        getAuthHeader()
      );
      setSuccessMsg(prev => ({ ...prev, [teamId]: res.data.entries_updated }));
      setGroups(prev => {
        const updated = { ...prev };
        for (const g of Object.keys(updated)) {
          updated[g] = updated[g].map(t =>
            t.id === teamId ? { ...t, goals_scored: goals } : t
          );
        }
        return updated;
      });
      setTimeout(() => setSuccessMsg(prev => ({ ...prev, [teamId]: null })), 3000);
    } catch (err) {
      console.error('Failed to update goals:', err);
    } finally {
      setUpdating(prev => ({ ...prev, [teamId]: false }));
    }
  };

  // Get points for a stage (non-cumulative)
  const getStagePoints = (stage) => {
    if (!stage) return 0;
    return pointsSystem[stage] || 0;
  };

  // Count teams at each stage
  const getStageSummary = () => {
    const summary = { not_started: 0, eliminated: 0 };
    stages.forEach(s => summary[s] = 0);
    Object.values(groups).flat().forEach(team => {
      const stage = team.current_stage;
      if (stage === 'eliminated') {
        summary.eliminated++;
      } else if (stage && summary[stage] !== undefined) {
        summary[stage]++;
      } else {
        summary.not_started++;
      }
    });
    return summary;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const stageSummary = getStageSummary();
  const sortedGroups = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div>
      {/* Stage Summary Bar */}
      <div className="card p-5 mb-6" data-testid="tournament-summary">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Trophy size={22} weight="duotone" /> Tournament Overview
          </h2>
          <button
            onClick={fetchTournament}
            className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5"
            data-testid="refresh-tournament-btn"
          >
            <ArrowClockwise size={16} /> Refresh
          </button>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 text-gray-600">
            Not Started: <strong>{stageSummary.not_started}</strong>
          </div>
          <div className={`px-3 py-1.5 rounded-lg text-sm ${stageSummary.eliminated > 0 ? 'bg-red-50 text-red-600 font-medium' : 'bg-gray-50 text-gray-400'}`}>
            Eliminated: <strong>{stageSummary.eliminated}</strong>
          </div>
          {stages.map(stage => (
            <div
              key={stage}
              className={`px-3 py-1.5 rounded-lg text-sm ${stageSummary[stage] > 0 ? 'bg-green-50 text-green-700 font-medium' : 'bg-gray-50 text-gray-400'}`}
            >
              {STAGE_LABELS[stage]}: <strong>{stageSummary[stage]}</strong>
              <span className="text-xs ml-1 opacity-60">({pointsSystem[stage]}pt)</span>
            </div>
          ))}
        </div>
      </div>

      {/* Points Reference */}
      <div className="card p-4 mb-6">
        <p className="text-xs text-gray-500 mb-2 font-semibold uppercase tracking-wide">Points Reference</p>
        <div className="flex flex-wrap gap-4 text-sm">
          {stages.map(stage => (
            <span key={stage} className="text-gray-600">
              {STAGE_LABELS[stage]}: <strong className="text-gray-900">{getStagePoints(stage)}pts</strong>
            </span>
          ))}
        </div>
      </div>

      {/* Groups Grid */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {sortedGroups.map(([groupName, groupTeams]) => (
          <div key={groupName} className="card p-4" data-testid={`tournament-group-${groupName}`}>
            <h3 className="overline mb-3 flex items-center gap-2">
              <Flag size={14} /> Group {groupName}
            </h3>
            <div className="space-y-2">
              {groupTeams.map(team => {
                const seedStyle = getSeedStyle(team.seed);
                const isUpdating = updating[team.id];
                const entries = successMsg[team.id];
                
                return (
                  <div
                    key={team.id}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    data-testid={`tournament-team-${team.country_code}`}
                  >
                    <img
                      src={getFlagUrl(team.country_code)}
                      alt={team.name}
                      className="w-6 h-4 object-cover rounded-sm shadow-sm flex-shrink-0"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <span className="font-medium text-sm flex-shrink-0 w-28 truncate" title={team.name}>
                      {team.name}
                    </span>
                    <span
                      className="text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ background: seedStyle.bg, color: seedStyle.color }}
                    >
                      {seedStyle.label}
                    </span>
                    <select
                      value={team.eliminated ? "eliminated" : (team.current_stage || "")}
                      onChange={(e) => updateProgress(team.id, e.target.value)}
                      disabled={isUpdating}
                      className={`flex-1 min-w-0 text-xs border rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-50 ${team.eliminated ? 'text-red-500' : ''}`}
                      style={{ borderColor: 'var(--border-color)' }}
                      data-testid={`stage-select-${team.country_code}`}
                    >
                      <option value="">Not Started</option>
                      <option value="eliminated">Didn't Qualify (0pts)</option>
                      {stages.map(stage => (
                        <option key={stage} value={stage}>
                          {STAGE_LABELS[stage]} ({getStagePoints(stage)}pts)
                        </option>
                      ))}
                    </select>
                    {/* Goals tally — leaderboard tiebreaker. Regulation + ET only. */}
                    <div className="flex items-center gap-1 flex-shrink-0" title="Total goals scored — tiebreaker (excludes penalty shootouts)">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">G</span>
                      <input
                        type="number"
                        min="0"
                        max="99"
                        defaultValue={team.goals_scored ?? 0}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value, 10) || 0;
                          if (val !== (team.goals_scored ?? 0)) updateGoals(team.id, val);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        disabled={isUpdating}
                        className="w-12 text-xs border rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-50 text-center"
                        style={{ borderColor: 'var(--border-color)' }}
                        data-testid={`goals-input-${team.country_code}`}
                      />
                    </div>
                    <div className="w-5 flex-shrink-0">
                      {isUpdating && (
                        <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#3B82F6', borderTopColor: 'transparent' }} />
                      )}
                      {entries !== null && entries !== undefined && !isUpdating && (
                        <Check size={16} weight="bold" className="text-green-500" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
